(() => {
  if (window.swQuickAddStripBound) return;
  window.swQuickAddStripBound = true;

  // The section that renders the cart drawer, so its markup can be re-rendered
  // after an add. Matches sections/cart-drawer-section.liquid.
  const CART_SECTION = 'cart-drawer-section';

  /* ---------------------------------------------------------------------- *
   * Button placement
   *
   * - Theme (collection / search) cards: appended inside .card-gallery, so it
   *   takes the photo's exact width (on mobile that box is pulled out to the
   *   screen edges) and sits flush under it.
   * - SW cards (rails, Recently Viewed, Discover More): the image box has a
   *   fixed 3:4 shape, so the button goes right after it instead.
   * ---------------------------------------------------------------------- */

  const placeStrips = () => {
    document.querySelectorAll('[data-sw-qa]:not([data-sw-qa-moved])').forEach((strip) => {
      const gallery = strip.parentElement?.closest('.card-gallery');
      if (gallery) {
        gallery.appendChild(strip);
      } else {
        const box = strip.parentElement?.closest('.sw-pcard__media, .product-card__media');
        if (box && box.parentElement) box.insertAdjacentElement('afterend', strip);
      }
      strip.dataset.swQaMoved = '1';
    });
  };

  /* ---------------------------------------------------------------------- *
   * Size pop-up
   * ---------------------------------------------------------------------- */

  const closeAllPops = (except) => {
    document.querySelectorAll('[data-sw-qa][data-open="true"]').forEach((strip) => {
      if (strip === except) return;
      strip.dataset.open = 'false';
      strip.querySelector('[data-sw-qa-open]')?.setAttribute('aria-expanded', 'false');
    });
  };

  const togglePop = (strip) => {
    const open = strip.dataset.open !== 'true';
    closeAllPops(strip);
    strip.dataset.open = open ? 'true' : 'false';
    strip.querySelector('[data-sw-qa-open]')?.setAttribute('aria-expanded', String(open));
  };

  /* ---------------------------------------------------------------------- *
   * Cart count
   * ---------------------------------------------------------------------- */

  const countElements = () => document.querySelectorAll('.cart-bubble__text-count');

  const setBubble = (count) => {
    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.classList.remove('visually-hidden');
    });
    countElements().forEach((el) => {
      el.classList.remove('hidden');
      el.textContent = count < 100 ? String(count) : '';
    });
  };

  const bumpBubble = () => {
    const first = countElements()[0];
    const current = first ? parseInt(first.textContent.trim(), 10) : 0;
    setBubble((Number.isNaN(current) ? 0 : current) + 1);
  };

  /* ---------------------------------------------------------------------- *
   * Cart + product data (cached so drawer size dropdowns appear instantly)
   * ---------------------------------------------------------------------- */

  const productCache = new Map();
  let itemsByKey = new Map();

  const getProduct = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  const resolvedProducts = new Map();

  const primeProduct = async (handle) => {
    if (resolvedProducts.has(handle)) return;
    const product = await getProduct(handle);
    if (product) resolvedProducts.set(handle, product);
  };

  const primeCart = async () => {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;

      const cart = await res.json();
      itemsByKey = new Map(cart.items.map((item) => [item.key, item]));

      await Promise.all([...new Set(cart.items.map((item) => item.handle))].map(primeProduct));

      return cart;
    } catch (e) {
      return null;
    }
  };

  /* ---------------------------------------------------------------------- *
   * Size picker inside the cart drawer
   * ---------------------------------------------------------------------- */

  const styleSelect = (select) => {
    Object.assign(select.style, {
      font: 'inherit',
      fontSize: '0.8125rem',
      color: 'inherit',
      background: 'transparent',
      border: '1px solid var(--color-border, #e7e2da)',
      borderRadius: 'var(--style-border-radius-buttons-primary, 2px)',
      padding: '4px 24px 4px 8px',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' fill='none' stroke='%23333' stroke-width='1.5'/></svg>\")",
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 7px center',
      backgroundSize: '9px',
    });
  };

  const changeSize = async (item, newId) => {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: newId, quantity: item.quantity }] }),
      });
    } catch (e) {
      /* the refresh below shows whatever actually happened */
    }

    refreshCart();
  };

  const decorateCartSizes = () => {
    const rows = document.querySelectorAll('.cart-items__table-row[data-key]');
    if (!rows.length) return;

    let missing = false;

    rows.forEach((row) => {
      if (row.dataset.swSizeBound === '1') return;

      const item = itemsByKey.get(row.dataset.key);
      const variants = row.querySelector('.cart-items__variants');
      if (!variants) return;

      if (!item) {
        missing = true;
        return;
      }

      const product = resolvedProducts.get(item.handle);
      if (!product) {
        missing = true;
        primeProduct(item.handle).then(decorateCartSizes);
        return;
      }

      const sizeIndex = product.options.findIndex((option) => {
        const name = typeof option === 'string' ? option : option.name;
        return String(name).toLowerCase().includes('size');
      });
      if (sizeIndex === -1) return;

      const current = product.variants.find((variant) => variant.id === item.variant_id);
      if (!current) return;

      const siblings = product.variants.filter((variant) =>
        variant.options.every((value, index) => index === sizeIndex || value === current.options[index])
      );
      if (siblings.length < 2) return;

      row.dataset.swSizeBound = '1';

      const select = document.createElement('select');
      select.className = 'sw-cart-size';
      select.setAttribute('aria-label', 'Change size');
      styleSelect(select);

      siblings.forEach((variant) => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = variant.options[sizeIndex] + (variant.available ? '' : ' \u2014 sold out');
        option.disabled = !variant.available;
        option.selected = variant.id === current.id;
        select.appendChild(option);
      });

      select.addEventListener('change', () => {
        const newId = Number(select.value);
        if (!newId || newId === current.id) return;
        select.disabled = true;
        changeSize(item, newId);
      });

      variants.replaceChildren(select);
    });

    if (missing) primeCart().then(decorateCartSizes);
  };

  /* ---------------------------------------------------------------------- *
   * Cart drawer
   * ---------------------------------------------------------------------- */

  const LIVE_PARTS = ['.cart-drawer__content', '.cart-drawer__summary', '.cart-drawer__header'];

  const cartHost = () => document.getElementById(`shopify-section-${CART_SECTION}`);
  const themeDrawer = () => cartHost()?.querySelector('theme-drawer');

  const isDrawerOpen = () => {
    const drawer = themeDrawer();
    if (drawer && typeof drawer.isOpen === 'boolean') return drawer.isOpen;
    return Boolean(cartHost()?.querySelector('dialog[open], theme-drawer[open]'));
  };

  const autoOpenEnabled = () => {
    const component = cartHost()?.querySelector('cart-drawer-component');
    return !component || component.hasAttribute('auto-open');
  };

  const openCartDrawer = () => {
    if (!autoOpenEnabled()) return;
    const drawer = themeDrawer();
    if (drawer && !isDrawerOpen() && typeof drawer.open === 'function') drawer.open();
  };

  /**
   * Shows the product in the drawer straight away, before the server has
   * answered — the round trip is what made the drawer open without the item
   * for a second or two.
   *
   * An existing line is cloned so the stand-in looks exactly like a real
   * one, then filled with this product's photo, name, size and price. If the
   * same size is already in the cart its quantity is bumped instead. The real
   * render replaces all of it a moment later.
   *
   * An empty cart has no line to copy, so it returns false and the caller
   * opens the drawer once the real contents arrive.
   *
   * @returns {boolean} whether the drawer now shows the item
   */
  const MONEY = /(?:Rs\.?|\u20b9)\s?[\d,]+(?:\.\d+)?/;

  const showLineNow = (strip, button) => {
    const rows = cartHost()?.querySelectorAll('.cart-items__table-row[data-key]');
    if (!rows || !rows.length) return false;

    const variantId = String(button.dataset.variantId);

    const existing = [...rows].find((row) => row.dataset.key.split(':')[0] === variantId);
    if (existing) {
      const qty = existing.querySelector('input[type="number"]');
      if (qty) qty.value = String((parseInt(qty.value, 10) || 0) + 1);
      return true;
    }

    const line = rows[0].cloneNode(true);
    line.removeAttribute('data-key');
    delete line.dataset.swSizeBound;
    line.dataset.swOptimistic = '1';

    const img = line.querySelector('img');
    if (img && strip.dataset.flyImage) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = strip.dataset.flyImage;
      img.alt = strip.dataset.title || '';
    }

    line.querySelectorAll('a[href]').forEach((a) => {
      if (strip.dataset.url) a.href = strip.dataset.url;
    });

    const title = line.querySelector('.cart-items__title');
    if (title) title.textContent = strip.dataset.title || '';

    const variants = line.querySelector('.cart-items__variants');
    if (variants) {
      const size = button.dataset.variantTitle || '';
      variants.textContent = size;
      variants.hidden = !size;
    }

    // Struck-through compare-at prices belong to the copied line.
    line.querySelectorAll('s, del, .compare-at-price, [class*="compare"]').forEach((el) => el.remove());

    if (button.dataset.price) {
      const walker = document.createTreeWalker(line, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        if (MONEY.test(node.nodeValue)) node.nodeValue = node.nodeValue.replace(MONEY, button.dataset.price);
      });
    }

    const qty = line.querySelector('input[type="number"]');
    if (qty) qty.value = '1';

    // Shopify puts newly added lines at the top of the cart.
    rows[0].parentElement.insertBefore(line, rows[0]);
    return true;
  };

  const renderCartSection = (html) => {
    if (!html) return;

    const target = cartHost();
    if (!target) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.getElementById(`shopify-section-${CART_SECTION}`) || parsed.body.firstElementChild;
    if (!source) return;

    const wasOpen = isDrawerOpen();

    if (wasOpen) {
      let swapped = false;

      LIVE_PARTS.forEach((selector) => {
        const to = target.querySelector(selector);
        const from = source.querySelector(selector);
        if (to && from) {
          to.innerHTML = from.innerHTML;
          swapped = true;
        }
      });

      if (swapped) {
        decorateCartSizes();
        return;
      }
    }

    target.innerHTML = source.innerHTML;
    if (wasOpen) themeDrawer()?.open?.();
    decorateCartSizes();
  };

  const refreshCart = async () => {
    const [cart] = await Promise.all([
      primeCart(),
      fetch(`${window.location.pathname}?section_id=${CART_SECTION}`)
        .then((res) => (res.ok ? res.text() : null))
        .then((html) => {
          if (html) renderCartSection(html);
        })
        .catch(() => {}),
    ]);

    if (cart) setBubble(cart.item_count || 0);
    decorateCartSizes();
  };

  /* ---------------------------------------------------------------------- *
   * Flying image
   * ---------------------------------------------------------------------- */

  const preloaded = new Set();

  const preload = (strip) => {
    const src = strip?.dataset.flyImage;
    if (!src || preloaded.has(src)) return;
    preloaded.add(src);
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
  };

  const flyToCart = (strip, origin) => {
    const image = strip.dataset.flyImage;
    const cartIcon =
      document.querySelector('.header-actions__cart-icon') ||
      document.querySelector('.cart-bubble') ||
      document.querySelector('[href$="/cart"]');

    if (!image || !cartIcon || !origin) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const from = origin.getBoundingClientRect();
    const to = cartIcon.getBoundingClientRect();

    const startX = from.left + from.width / 2;
    const startY = from.top + from.height / 2;
    const endX = to.left + to.width / 2;
    const endY = to.top + to.height / 2;

    const flyer = document.createElement('div');
    flyer.className = 'sw-fly';
    flyer.style.backgroundImage = `url(${image})`;
    document.body.appendChild(flyer);

    const animation = flyer.animate(
      [
        { transform: `translate(${startX}px, ${startY}px) translate(-50%, -50%) scale(1)`, opacity: 1 },
        {
          transform: `translate(${(startX + endX) / 2}px, ${Math.min(startY, endY) - 80}px) translate(-50%, -50%) scale(0.72)`,
          opacity: 0.95,
          offset: 0.55,
        },
        { transform: `translate(${endX}px, ${endY}px) translate(-50%, -50%) scale(0.16)`, opacity: 0.15 },
      ],
      { duration: 520, easing: 'cubic-bezier(0.32, 0, 0.5, 1)', fill: 'forwards' }
    );

    animation.onfinish = () => {
      flyer.remove();
      cartIcon.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.18)' }, { transform: 'scale(1)' }],
        { duration: 260, easing: 'ease-out' }
      );
    };
    animation.oncancel = () => flyer.remove();
  };

  /* ---------------------------------------------------------------------- *
   * Interaction
   * ---------------------------------------------------------------------- */

  ['pointerenter', 'touchstart'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const strip = event.target.closest?.('[data-sw-qa]');
        if (strip) preload(strip);
      },
      { capture: true, passive: true }
    );
  });

  document.addEventListener(
    'click',
    async (event) => {
      const opener = event.target.closest('[data-sw-qa-open]');
      if (opener) {
        event.preventDefault();
        event.stopPropagation();
        const strip = opener.closest('[data-sw-qa]');
        preload(strip);
        togglePop(strip);
        return;
      }

      const closer = event.target.closest('[data-sw-qa-close]');
      if (closer) {
        event.preventDefault();
        event.stopPropagation();
        closeAllPops(null);
        return;
      }

      const addButton = event.target.closest('[data-sw-qa-add]');
      if (!addButton) {
        if (!event.target.closest('[data-sw-qa-pop]')) closeAllPops(null);
        if (event.target.closest('[data-sw-qa]')) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const strip = addButton.closest('[data-sw-qa]');

      addButton.dataset.selected = 'true';
      flyToCart(strip, addButton);
      bumpBubble();

      // Put the item in the drawer first, then open it, so it is already
      // there the moment the drawer slides in.
      const shownEarly = showLineNow(strip, addButton);
      if (shownEarly) openCartDrawer();

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(addButton.dataset.variantId), quantity: 1 }],
            sections: CART_SECTION,
            sections_url: window.location.pathname,
          }),
        });

        if (!res.ok) throw new Error('add failed');

        const data = await res.json();

        // Cache the new line first so its size dropdown lands with it.
        await primeCart();
        renderCartSection(data?.sections?.[CART_SECTION]);
        if (!shownEarly) openCartDrawer();

        refreshCart();
      } catch (e) {
        refreshCart();
      } finally {
        setTimeout(() => {
          delete addButton.dataset.selected;
          closeAllPops(null);
        }, 350);
      }
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllPops(null);
  });

  /* ---------------------------------------------------------------------- *
   * Start-up
   * ---------------------------------------------------------------------- */

  const start = () => {
    placeStrips();
    primeCart().then(decorateCartSizes);

    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        placeStrips();
        decorateCartSizes();
      });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
